import React, { useMemo, useState, useEffect } from 'react';
import { Layout, Menu, Drawer, Avatar, Dropdown, ConfigProvider, Breadcrumb, Badge } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined, UserOutlined, LogoutOutlined, BellOutlined, QrcodeOutlined } from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import useAuthContext from '../../hooks/useAuthContext';
import { getMenuItemsByUser } from '../../config/menuAdminConfig';
import Logo from '../../assets/img/LogoÑuñoa.png';
import { FiHome, FiGrid, FiMenu, FiUser, FiLogOut, FiChevronRight, FiX } from 'react-icons/fi';
import Acceso from '../../service/Acceso';
import QRAccesoModal from '../Acceso/QRAccesoModal';

const { Sider, Content } = Layout;

/* ── Helpers ── */
const ROLE_LABEL = {
  isAdmin: 'Administrador',
  isCoordinador: 'Coordinador',
  isVentas: 'Jefe Ventas',
  isJefe_recinto: 'Jefe Recinto',
  isReporte: 'Reportería',
};
const ROLE_COLOR = {
  isAdmin: '#0047bb',
  isCoordinador: '#3b82f6',
  isVentas: '#f97316',
  isJefe_recinto: '#a855f7',
  isReporte: '#06b6d4',
};
// Sidebar en el azul marino de marca (el mismo del footer y las cabeceras); antes era
// un gris pizarra (#0f172a) que no pertenecía a la paleta. Texto sobre él: 8.6:1.
const SIDEBAR_BG = '#00266b';
const SIDEBAR_TEXTO = '#b9c8e8';

/**
 * Variante clara del color de cada rol, para usar como TEXTO sobre el sidebar.
 * Los tonos de ROLE_COLOR funcionan como relleno del avatar, pero como texto no
 * llegan al mínimo AA sobre azul marino. Estos superan 6:1.
 */
const ROLE_TEXT_COLOR = {
  isAdmin: '#6ea8ff',
  isCoordinador: '#93c5fd',
  isVentas: '#fdba74',
  isJefe_recinto: '#d8b4fe',
  isReporte: '#67e8f9',
};

/**
 * Etiqueta y color del rol para la cabecera del panel.
 *
 * Los cinco roles originales conservan su color; para cualquier otro se usa el nombre
 * que trae la sesión (`rolNombre`, de la colección `roles`). Sin eso, un rol creado
 * desde el panel aparecía como "Usuario" a secas.
 */
const getRole = (user) => {
  if (!user) return { label: 'Usuario', color: '#0047bb', textColor: '#6ea8ff' };
  const key = Object.keys(ROLE_LABEL).find(k => user[k]);
  if (key) return { label: ROLE_LABEL[key], color: ROLE_COLOR[key], textColor: ROLE_TEXT_COLOR[key] };
  return { label: user.rolNombre || user.role || 'Usuario', color: '#0047bb', textColor: '#6ea8ff' };
};

const crumbLabels = {
  talleres: 'Talleres', lista: 'Lista', crear: 'Crear', editar: 'Editar',
  usuarios: 'Usuarios', recintos: 'Recintos', matriculas: 'Matrículas',
  configuracion: 'Configuración', estadisticas: 'Estadísticas',
  'descuentos-automaticos': 'Descuentos', reporteria: 'Reportería',
  dashboard: 'Inicio', ventas: 'Ventas', nueva: 'Nueva Venta',
  financiero: 'Financiero', ingresos: 'Ingresos', historial: 'Historial',
  'cambios-revisiones': 'Cambios y Revisiones', manual: 'Registro Manual',
  comunicaciones: 'Comunicaciones', redactar: 'Redactar',
  operaciones: 'Operaciones', agenda: 'Agendamiento', reservas: 'Reservas',
  servicios: 'Servicios', profesionales: 'Profesionales y boxes',
};

const useBreadcrumbs = (pathname) => useMemo(() => {
  const parts = pathname.replace(/^\/admin\/?/, '').split('/').filter(Boolean);
  const crumbs = [{ title: 'Admin', path: '/admin/talleres/lista' }];
  let path = '/admin';
  for (const p of parts) {
    path += `/${p}`;
    if (/^[0-9a-f-]{24,}$/i.test(p)) continue;
    const label = crumbLabels[p] || p.charAt(0).toUpperCase() + p.slice(1);
    crumbs.push({ title: label, path });
  }
  return crumbs;
}, [pathname]);

/* ─────────────────────────────────── */
const AdminLayout = () => {
  const [collapsed, setCollapsed]   = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile]     = useState(window.innerWidth < 1024);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrLoading, setQrLoading]   = useState(false);
  const [qrData, setQrData]         = useState(null);
  const [qrError, setQrError]       = useState(null);
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, logout } = useAuthContext();

  const handleGenerarQR = async () => {
    setQrModalOpen(true);
    setQrLoading(true);
    setQrError(null);
    setQrData(null);
    try {
      const data = await Acceso.GenerarQR();
      setQrData(data);
    } catch (error) {
      setQrError(error.message || 'No se pudo generar el QR');
    } finally {
      setQrLoading(false);
    }
  };

  const role = useMemo(() => getRole(user), [user]);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', fn, { passive: true });
    return () => window.removeEventListener('resize', fn);
  }, []);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const selectedKey = useMemo(() => {
    const p = location.pathname.replace(/^\/admin\/?/, '');
    return p || 'talleres/lista';
  }, [location.pathname]);

  /**
   * Submenús que deben estar abiertos según la ruta actual.
   *
   * Las claves del menú son el path relativo ('reporteria/financiero/ingresos'),
   * así que basta acumular los prefijos de todos los segmentos salvo el último:
   *   reporteria/financiero/ingresos → ['reporteria', 'reporteria/financiero']
   *   talleres/lista                 → ['talleres']
   *   dashboard                      → []
   *
   * La versión anterior encadenaba `if (p.startsWith(...))` y las tres ramas de
   * submenú anidado eran inalcanzables: cualquier 'reporteria/financiero/...'
   * entraba primero por 'reporteria/', así que el subgrupo nunca se abría y el
   * ítem activo quedaba escondido.
   */
  const derivedOpenKeys = useMemo(() => {
    const parts = location.pathname.replace(/^\/admin\/?/, '').split('/').filter(Boolean);
    return parts.slice(0, -1).map((_, i) => parts.slice(0, i + 1).join('/'));
  }, [location.pathname]);

  /**
   * `openKeys` controlado. Antes se pasaba a `defaultOpenKeys`, que antd lee una
   * sola vez al montar: el recálculo por ruta no tenía ningún efecto y el
   * submenú solo aparecía abierto si la app cargaba directamente en esa ruta.
   * Con `onOpenChange` el usuario además puede abrir y cerrar a mano.
   */
  const [openKeys, setOpenKeys] = useState(derivedOpenKeys);
  useEffect(() => { setOpenKeys(derivedOpenKeys); }, [derivedOpenKeys]);

  const menuItems = useMemo(() => user ? getMenuItemsByUser(user) : [], [user]);
  const breadcrumbs = useBreadcrumbs(location.pathname);

  const userMenu = {
    items: [
      { key: 'perfil',  label: 'Mi Perfil',    icon: <UserOutlined />,  onClick: () => navigate('/perfil') },
      { key: 'qr',      label: 'Mi QR de acceso', icon: <QrcodeOutlined />, onClick: handleGenerarQR },
      { key: 'home',    label: 'Ir al inicio',  icon: <FiHome size={14} className="inline" />, onClick: () => navigate('/') },
      { type: 'divider' },
      { key: 'logout',  label: 'Cerrar sesión', icon: <LogoutOutlined />, danger: true, onClick: logout },
    ],
  };

  /* ── Sidebar content (shared between Sider and Drawer) ── */
  const SideContent = () => (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: SIDEBAR_BG }}>

      {/* Logo */}
      <div style={{
        height: 64, display: 'flex', alignItems: 'center',
        padding: collapsed && !isMobile ? '0 20px' : '0 20px',
        borderBottom: '1px solid rgba(255,255,255,.06)', flexShrink: 0, gap: 10,
        justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
      }}>
        {/* El escudo es azul con anillo y emblema amarillos: sobre el sidebar
            oscuro se lee solo. El filtro anterior ('brightness(0) invert(1)')
            lo aplastaba a una silueta blanca sin detalle. */}
        <img
          src={Logo} alt="Ir al sitio público de Ñuñoa Deportes"
          onClick={() => navigate('/')}
          style={{ height: 36, width: 'auto', objectFit: 'contain', cursor: 'pointer' }}
        />
        {(!collapsed || isMobile) && (
          <div style={{ lineHeight: 1.3 }}>
            {/* Amarillo de marca sobre azul marino: 11:1. */}
            <div style={{ fontWeight: 800, color: '#feda2c', fontSize: 13, letterSpacing: '.3px' }}>Panel Admin</div>
            <div style={{ color: SIDEBAR_TEXTO, fontSize: 11, fontWeight: 600 }}>Ñuñoa Deportes</div>
          </div>
        )}
      </div>

      {/* Menu */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingTop: 8 }} className="custom-scrollbar">
        <Menu
          mode="inline"
          theme="dark"
          selectedKeys={[selectedKey]}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          onClick={({ key }) => navigate(`/admin/${key}`)}
          items={menuItems}
          style={{
            background: 'transparent', borderRight: 'none',
            padding: '4px 8px', paddingBottom: 80,
          }}
          className="admin-dark-menu"
        />
      </div>

      {/* User info */}
      {user && (!collapsed || isMobile) && (
        <div style={{
          padding: '14px 16px', borderTop: '1px solid rgba(255,255,255,.06)',
          background: 'rgba(255,255,255,.03)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar size={34} icon={<UserOutlined />}
              style={{ background: role.color, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#f8fafc', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.name}
              </p>
              {/* Variante clara: el color de relleno del avatar no alcanza AA como texto. */}
              <p style={{ fontSize: 11, color: role.textColor, fontWeight: 600, margin: 0 }}>{role.label}</p>
            </div>
          </div>
        </div>
      )}

      {/* Collapse button — desktop only */}
      {!isMobile && (
        <button
          onClick={() => setCollapsed(c => !c)}
          style={{
            height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,.04)', color: SIDEBAR_TEXTO, border: 'none',
            cursor: 'pointer', fontSize: 16, flexShrink: 0,
            borderTop: '1px solid rgba(255,255,255,.06)', transition: 'all .2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(254,218,44,.12)'; e.currentTarget.style.color = '#feda2c'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,.04)'; e.currentTarget.style.color = SIDEBAR_TEXTO; }}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </button>
      )}
    </div>
  );

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: '#0047bb', borderRadius: 10 },
        components: {
          Layout: { siderBg: SIDEBAR_BG },
          Menu: {
            darkItemBg: 'transparent',
            darkSubMenuItemBg: 'rgba(255,255,255,.04)',
            darkItemColor: SIDEBAR_TEXTO,
            darkItemHoverBg: 'rgba(255,255,255,.08)',
            darkItemHoverColor: '#ffffff',
            // Ítem activo en amarillo con texto azul marino (11:1), como el resto del sitio.
            darkItemSelectedBg: '#feda2c',
            darkItemSelectedColor: '#00266b',
            darkPopupBg: '#00337f',
            itemBorderRadius: 8,
            itemMarginInline: 4,
            itemMarginBlock: 1,
            iconSize: 16,
            collapsedIconSize: 18,
            fontSize: 13,
          },
        },
      }}
    >
      <Layout style={{ minHeight: '100vh', background: '#f1f5f9' }}>

        {/* ── Desktop sidebar ── */}
        {!isMobile && (
          <Sider
            width={240} collapsedWidth={64} collapsed={collapsed} trigger={null}
            style={{ position: 'sticky', top: 0, height: '100vh', background: SIDEBAR_BG, boxShadow: '2px 0 12px rgba(0,0,0,.18)' }}
          >
            <SideContent />
          </Sider>
        )}

        {/* ── Mobile drawer ── */}
        {isMobile && (
          <Drawer
            placement="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}
            width={260}
            styles={{ body: { padding: 0, background: SIDEBAR_BG }, header: { display: 'none' } }}
          >
            <div style={{ height: '100vh', background: SIDEBAR_BG }}>
              <SideContent />
            </div>
          </Drawer>
        )}

        <Layout style={{ background: '#f1f5f9' }}>

          {/* ── Header ── */}
          <div style={{
            position: 'sticky', top: 0, zIndex: 100,
            background: '#fff', borderBottom: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,.05)',
            height: 56, display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', padding: '0 16px 0 12px', gap: 12,
          }}>

            {/* Left */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <button
                onClick={() => isMobile ? setDrawerOpen(o => !o) : setCollapsed(c => !c)}
                style={{
                  width: 36, height: 36, border: 'none', background: 'transparent', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 8, color: '#64748b', transition: 'all .2s', flexShrink: 0,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#0f172a'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
              >
                {isMobile ? <FiMenu size={20} /> : collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              </button>

              {/* Mobile logo */}
              {isMobile && (
                <img src={Logo} alt="Logo" style={{ height: 28, width: 'auto', cursor: 'pointer' }} onClick={() => navigate('/')} />
              )}

              {/* Breadcrumb — desktop */}
              {!isMobile && (
                <Breadcrumb
                  separator={<FiChevronRight size={12} style={{ color: '#cbd5e1', verticalAlign: 'middle' }} />}
                  items={breadcrumbs.map((b, i) => ({
                    title: i === breadcrumbs.length - 1
                      ? <span style={{ color: '#0f172a', fontWeight: 700, fontSize: 13 }}>{b.title}</span>
                      : <span style={{ color: '#94a3b8', fontSize: 13, cursor: 'pointer' }} onClick={() => navigate(b.path)}>{b.title}</span>,
                  }))}
                />
              )}
            </div>

            {/* Right */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>

              {/* Ir al inicio */}
              <button
                onClick={() => navigate('/')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', borderRadius: 20,
                  border: '1px solid #e2e8f0', background: 'transparent',
                  cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#64748b',
                  transition: 'all .2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#0047bb'; e.currentTarget.style.color = '#0047bb'; e.currentTarget.style.background = '#eff6ff'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = 'transparent'; }}
              >
                <FiHome size={14} />
                <span className="hidden sm:inline">Inicio</span>
              </button>

              {/* User dropdown */}
              <Dropdown menu={userMenu} trigger={['click']} placement="bottomRight">
                <button
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '4px 10px 4px 4px', border: '1px solid #e2e8f0',
                    background: 'transparent', cursor: 'pointer', borderRadius: 24,
                    transition: 'all .2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                >
                  <Avatar size={30} icon={<UserOutlined />}
                    style={{ background: role.color, flexShrink: 0 }} />
                  {!isMobile && (
                    <div style={{ textAlign: 'left' }}>
                      <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                        {user?.name?.split(' ')[0] || 'Admin'}
                      </p>
                      <p style={{ margin: 0, fontSize: 10, color: role.color, lineHeight: 1.2, fontWeight: 600 }}>{role.label}</p>
                    </div>
                  )}
                </button>
              </Dropdown>
            </div>
          </div>

          {/* ── Content ── */}
          <Content style={{ padding: isMobile ? '12px 8px 80px' : '20px' }}>
            <Outlet />
          </Content>

        </Layout>
      </Layout>

      {/* ── QR de acceso al recinto (staff) ── */}
      <QRAccesoModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        loading={qrLoading}
        error={qrError}
        data={qrData}
        onRegenerar={handleGenerarQR}
      />

      {/* ── Mobile bottom nav ── */}
      {isMobile && (
        <nav style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: SIDEBAR_BG, borderTop: '3px solid #feda2c',
          boxShadow: '0 -4px 20px rgba(0,0,0,.25)',
          display: 'flex', zIndex: 200,
        }}>
          {[
            { icon: <FiHome size={20} />,  label: 'Inicio',   action: () => navigate('/') },
            { icon: <FiGrid size={20} />,  label: 'Talleres', action: () => navigate('/admin/talleres/lista') },
            { icon: <FiMenu size={20} />,  label: 'Menú',     action: () => setDrawerOpen(true) },
            {
              icon: <Avatar size={22} icon={<FiUser size={12} />} style={{ background: role.color }} />,
              label: 'Perfil',
              action: () => navigate('/perfil'),
            },
          ].map((item, i) => (
            <button key={i} onClick={item.action} style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', padding: '10px 4px', border: 'none',
              background: 'transparent', cursor: 'pointer', gap: 3, color: SIDEBAR_TEXTO,
              transition: 'color .2s',
            }}
              onMouseEnter={e => e.currentTarget.style.color = '#feda2c'}
              onMouseLeave={e => e.currentTarget.style.color = SIDEBAR_TEXTO}
            >
              {item.icon}
              <span style={{ fontSize: 10, fontWeight: 600 }}>{item.label}</span>
            </button>
          ))}
        </nav>
      )}
    </ConfigProvider>
  );
};

export default AdminLayout;
